/**
 * AES-CBC 加解密工具
 * 使用 Web Crypto API，兼容 Electron 渲染进程
 */

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/**
 * 将 base64 字符串转换为 Uint8Array
 */
function base64ToUint8Array(base64) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * 将 Uint8Array 转换为 base64 字符串
 */
function uint8ArrayToBase64(array) {
  return btoa(String.fromCharCode(...array));
}

/**
 * 将 UTF-8 字符串转换为 Uint8Array
 */
function stringToUint8Array(str) {
  return textEncoder.encode(str);
}

/**
 * 导入 AES-GCM 密钥（从 UTF-8 字符串）- 保留用于兼容
 * @deprecated 请使用 importAesKeyCBC
 */
async function importAesKey(keyStr) {
  try {
    const rawKey = stringToUint8Array(keyStr);
    const key = await crypto.subtle.importKey(
      'raw',
      rawKey,
      { name: 'AES-GCM', length: rawKey.length * 8 },
      false,
      ['encrypt', 'decrypt']
    );
    return key;
  } catch (e) {
    console.error('AES-GCM 密钥导入失败:', e);
    throw e;
  }
}

/**
 * 导入 AES-CBC 密钥（从 UTF-8 字符串）
 */
async function importAesKeyCBC(keyStr) {
  try {
    const rawKey = stringToUint8Array(keyStr);
    const key = await crypto.subtle.importKey(
      'raw',
      rawKey,
      { name: 'AES-CBC', length: rawKey.length * 8 },
      false,
      ['encrypt', 'decrypt']
    );
    return key;
  } catch (e) {
    console.error('AES-CBC 密钥导入失败:', e);
    throw e;
  }
}

/**
 * AES-GCM 加密 - 保留用于兼容
 * @deprecated 请使用 encryptDataCBC
 * @param {CryptoKey} key - AES 密钥
 * @param {string} plaintext - 明文字符串
 * @returns {string} base64 编码的加密数据 (IV + ciphertext + authTag)
 */
async function encryptData(key, plaintext) {
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV
  const plaintextBytes = textEncoder.encode(plaintext);
  
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    plaintextBytes
  );
  
  // 组合 IV + ciphertext (包含 authTag)
  const encryptedBytes = new Uint8Array(encrypted);
  const result = new Uint8Array(iv.length + encryptedBytes.length);
  result.set(iv, 0);
  result.set(encryptedBytes, iv.length);
  
  return uint8ArrayToBase64(result);
}

/**
 * AES-GCM 解密 - 保留用于兼容
 * @deprecated 请使用 decryptDataCBC
 * @param {CryptoKey} key - AES 密钥
 * @param {string} base64Data - base64 编码的加密数据
 * @returns {string} 解密后的明文字符串
 */
async function decryptData(key, base64Data) {
  const data = base64ToUint8Array(base64Data);
  const iv = data.slice(0, 12);
  const ciphertext = data.slice(12);
  
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );
  
  return textDecoder.decode(decrypted);
}

/**
 * AES-CBC 加密
 * @param {CryptoKey} key - AES 密钥
 * @param {string} plaintext - 明文字符串
 * @returns {string} base64 编码的加密数据 (IV + ciphertext)
 */
async function encryptDataCBC(key, plaintext) {
  const iv = crypto.getRandomValues(new Uint8Array(16)); // CBC 使用 16 字节 IV
  const plaintextBytes = textEncoder.encode(plaintext);
  
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    key,
    plaintextBytes
  );
  
  // 组合 IV + ciphertext
  const encryptedBytes = new Uint8Array(encrypted);
  const result = new Uint8Array(iv.length + encryptedBytes.length);
  result.set(iv, 0);
  result.set(encryptedBytes, iv.length);
  
  return uint8ArrayToBase64(result);
}

/**
 * AES-CBC 解密
 * @param {CryptoKey} key - AES 密钥
 * @param {string} base64Data - base64 编码的加密数据 (IV + ciphertext)
 * @returns {string} 解密后的明文字符串
 */
async function decryptDataCBC(key, base64Data) {
  const data = base64ToUint8Array(base64Data);
  const iv = data.slice(0, 16); // CBC 使用 16 字节 IV
  const ciphertext = data.slice(16);
  
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv },
    key,
    ciphertext
  );
  
  return textDecoder.decode(decrypted);
}

/**
 * 加密 JSON 对象 - 保留用于兼容
 * @deprecated 请使用 encryptJsonWithRawKeyCBC
 * @param {string} hexKey - hex 格式的 AES 密钥
 * @param {object} obj - 要加密的 JSON 对象
 * @returns {object} 加密后的对象 {enc: 'aes-gcm-v1', data: '<base64>'}
 */
export async function encryptJsonWithRawKey(hexKey, obj) {
  const key = await importAesKey(hexKey);
  const plaintext = JSON.stringify(obj);
  const encrypted = await encryptData(key, plaintext);
  return {
    enc: 'aes-gcm-v1',
    data: encrypted
  };
}

/**
 * 解密 JSON 对象 (AES-GCM) - 保留用于兼容
 * @deprecated 请使用 decryptJsonWithRawKeyCBC
 * @param {string} hexKey - hex 格式的 AES 密钥
 * @param {string} dataB64 - base64 编码的加密数据
 * @returns {object} 解密后的 JSON 对象
 */
export async function decryptJsonWithRawKey(hexKey, dataB64) {
  const key = await importAesKey(hexKey);
  const plaintext = await decryptData(key, dataB64);
  return JSON.parse(plaintext);
}

/**
 * 加密 JSON 对象 (AES-CBC)
 * @param {string} hexKey - hex 格式的 AES 密钥
 * @param {object} obj - 要加密的 JSON 对象
 * @returns {object} 加密后的对象 {enc: 'aes-cbc-v1', data: '<base64>'}
 */
export async function encryptJsonWithRawKeyCBC(hexKey, obj) {
  const key = await importAesKeyCBC(hexKey);
  const plaintext = JSON.stringify(obj);
  const encrypted = await encryptDataCBC(key, plaintext);
  return {
    enc: 'aes-cbc-v1',
    data: encrypted
  };
}

/**
 * 解密 JSON 对象 (AES-CBC)
 * @param {string} hexKey - hex 格式的 AES 密钥
 * @param {string} dataB64 - base64 编码的加密数据
 * @returns {object} 解密后的 JSON 对象
 */
export async function decryptJsonWithRawKeyCBC(hexKey, dataB64) {
  const key = await importAesKeyCBC(hexKey);
  const plaintext = await decryptDataCBC(key, dataB64);
  return JSON.parse(plaintext);
}

/**
 * 加密 HTTP 请求体
 * @param {object} data - 请求数据
 * @param {string} mode - 加密模式 'gcm' 或 'cbc'，默认 'cbc'
 * @returns {object} 加密后的请求体
 */
export async function encryptRequestBody(data, mode = 'cbc') {
  const aesKey = process.env.VUE_APP_AES_KEY;
  if (!aesKey || typeof data !== 'object') {
    return data;
  }
  if (mode === 'cbc') {
    return await encryptJsonWithRawKeyCBC(aesKey, data);
  }
  return await encryptJsonWithRawKey(aesKey, data);
}

/**
 * 解密 HTTP 响应体
 * @param {object} response - 响应数据
 * @returns {object} 解密后的响应数据
 */
export async function decryptResponseBody(response) {
  const aesKey = process.env.VUE_APP_AES_KEY;
  if (!aesKey || !response || typeof response !== 'object') {
    return response;
  }
  // 检查是否是加密格式
  if (response.enc === 'aes-gcm-v1' && response.data) {
    return await decryptJsonWithRawKey(aesKey, response.data);
  }
  // 支持 AES-CBC 格式
  if (response.enc === 'aes-cbc-v1' && response.data) {
    return await decryptJsonWithRawKeyCBC(aesKey, response.data);
  }
  return response;
}

export default {
  encryptJsonWithRawKey,
  encryptJsonWithRawKeyCBC,
  decryptJsonWithRawKey,
  decryptJsonWithRawKeyCBC,
  encryptRequestBody,
  decryptResponseBody
};